import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { LogoIcon } from "@/components/Logo";
import PasswordStrength from "@/components/PasswordStrength";
import api from "@/lib/api";

const KVKK_TEXT = `Leranio KVKK Aydınlatma Metni (Yer Tutucu)\n\n6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) çerçevesinde; ad-soyad, e-posta, rol, sınıf, okul ve kullanım verileri; hizmet sunumu, hesap yönetimi, güvenlik ve iletişim amaçlarıyla işlenir. Verileriniz açık rıza olmaksızın üçüncü kişilerle paylaşılmaz. KVKK'nın 11. maddesi kapsamındaki haklarınızı kvkk@leranio.ai adresine yazarak kullanabilirsiniz. Detaylı metin yakında eklenecektir.`;
const TOS_TEXT = `Leranio Kullanım Şartları (Yer Tutucu)\n\nBu platformu kullanarak aşağıdaki kurallara uymayı kabul edersiniz: hizmeti yalnızca hukuka uygun eğitim amacıyla kullanmak; hesabınızı ve kimlik bilgilerinizi başkalarıyla paylaşmamak; başka kullanıcıların haklarını ihlal edecek içerik yayımlamamak; platformun kararlılığını ya da güvenliğini bozacak eylemlerde bulunmamak. Detaylı metin yakında eklenecektir.`;

export default function Register() {
  const { register, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", password2: "", role: "student", grade: "", school_id: "", kvkk_consent: false });
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [schools, setSchools] = useState([]);
  const [modal, setModal] = useState(null); // 'kvkk' | 'tos'

  useEffect(() => {
    api.get("/schools/public").then((r) => setSchools(r.data || [])).catch(() => setSchools([]));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error("Şifreler eşleşmiyor"); return; }
    if (form.password.length < 8) { toast.error("Şifre en az 8 karakter olmalı"); return; }
    if (!/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(form.password) || !/\d/.test(form.password)) { toast.error("Şifre en az bir harf ve bir rakam içermeli"); return; }
    if (!form.kvkk_consent) { toast.error("KVKK Aydınlatma Metni ve Kullanım Şartları'nı onaylamalısınız"); return; }
    if (!form.school_id) { toast.error("Okul seçimi zorunludur"); return; }
    try {
      const payload = { ...form };
      delete payload.password2;
      await register(payload);
      toast.success("Hesap oluşturuldu. E-postanı doğrula.");
      nav("/panel");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kayıt başarısız");
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 md:p-10 ambient-shadow">
        <Link to="/" className="flex items-center gap-2.5 mb-6">
          <LogoIcon size={40} />
          <span className="font-display font-bold text-xl">Leranio</span>
        </Link>
        <h2 className="font-display text-2xl font-bold mb-1">Yeni hesap oluştur</h2>
        <p className="text-sm text-muted-foreground mb-6">Ücretsiz başla, kredi kartı gerekmez.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div className="space-y-1.5">
            <Label htmlFor="name">Ad Soyad</Label>
            <Input id="name" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} required aria-required="true" data-testid="register-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">E-posta</Label>
            <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} required aria-required="true" data-testid="register-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw">Şifre</Label>
            <div className="relative">
              <Input id="pw" type={showPw ? "text" : "password"} autoComplete="new-password" minLength={10} value={form.password} onChange={(e) => set("password", e.target.value)} required aria-required="true" data-testid="register-password-input" className="pr-10" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <PasswordStrength value={form.password} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw2">Şifre Tekrar</Label>
            <div className="relative">
              <Input id="pw2" type={showPw2 ? "text" : "password"} autoComplete="new-password" minLength={10} value={form.password2} onChange={(e) => set("password2", e.target.value)} required aria-required="true" data-testid="register-password2-input" className="pr-10" />
              <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label={showPw2 ? "Şifreyi gizle" : "Şifreyi göster"}>
                {showPw2 ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {form.password2 && form.password !== form.password2 && <p className="text-xs text-red-600" data-testid="register-pw-mismatch">Şifreler eşleşmiyor</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Öğrenci</SelectItem>
                  <SelectItem value="parent">Veli</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "student" && (
              <div className="space-y-1.5">
                <Label>Sınıf</Label>
                <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
                  <SelectTrigger data-testid="register-grade-select"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    {["5","6","7","8","9","10","11","12"].map((g) => <SelectItem key={g} value={g}>{g}. Sınıf</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Okul</Label>
            <Select value={form.school_id} onValueChange={(v) => set("school_id", v)}>
              <SelectTrigger data-testid="register-school-select"><SelectValue placeholder="Okulunu seç" /></SelectTrigger>
              <SelectContent>
                {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start gap-2 pt-1">
            <Checkbox id="kvkk" checked={form.kvkk_consent} onCheckedChange={(v) => set("kvkk_consent", !!v)} data-testid="register-kvkk-checkbox" />
            <label htmlFor="kvkk" className="text-xs text-muted-foreground leading-relaxed">
              <button type="button" onClick={() => setModal("kvkk")} className="text-foreground underline hover:no-underline" data-testid="open-kvkk-modal">KVKK Aydınlatma Metni</button>
              {" ve "}
              <button type="button" onClick={() => setModal("tos")} className="text-foreground underline hover:no-underline" data-testid="open-tos-modal">Kullanım Şartları</button>
              {" \u2019nı okudum, kabul ediyorum."}
            </label>
          </div>
          <Button type="submit" className="w-full h-11 rounded-xl" disabled={loading} data-testid="register-submit-btn">
            {loading ? "Oluşturuluyor..." : "Hesap Oluştur"}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Zaten hesabın var mı? <Link to="/giris" className="text-foreground font-medium hover:underline">Giriş yap</Link>
        </div>
      </Card>

      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent data-testid="legal-modal">
          <DialogHeader><DialogTitle>{modal === "kvkk" ? "KVKK Aydınlatma Metni" : "Kullanım Şartları"}</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground whitespace-pre-line max-h-[60vh] overflow-y-auto">
            {modal === "kvkk" ? KVKK_TEXT : TOS_TEXT}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
